// Schemas and types for the cad tool, shared by server and UI.
import { z } from 'zod';

export const cadOpenInput = z.object({ fileId: z.uuid() });
export type CadOpenInput = z.infer<typeof cadOpenInput>;

/** Largest drawing the server will open or convert. */
export const CAD_MAX_SOURCE_BYTES = 80 * 1024 * 1024;

/** Conversion notes the UI turns into plain-language hints. */
export interface CadOpenWarning {
  code: 'conversion_errors' | 'conversion_warnings' | 'unusual_version';
  count: number;
}

export interface CadOpenResponse {
  name: string;
  format: 'dxf' | 'dwg';
  /**
   * Authenticated GET URL of the DXF bytes (DWG sources are converted on the server). The bytes travel
   * separately from this JSON so the server never builds a drawing-sized string; `v` pins the exact
   * file version this response describes.
   */
  dxfUrl: string;
  /** WHATWG text encoding label of those bytes, e.g. "utf-8" or "windows-1254". */
  dxfEncoding: string;
  converted: boolean;
  /** DWG release code such as "AC1032", when the source is a DWG. */
  version: string | null;
  warnings: CadOpenWarning[];
}

/** Error codes of /api/cad/open, so the UI can explain each one. */
export const CAD_ERROR_CODES = [
  'unsupported_type',
  'file_too_large',
  'corrupt_file',
  'binary_dxf',
  'conversion_failed',
  'conversion_timeout',
  'tool_missing',
  'file_changed',
] as const;
export type CadErrorCode = (typeof CAD_ERROR_CODES)[number];
